import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Catalogue from "@/pages/Catalogue";
import EInvoicingService from "@/pages/EInvoicingService";
import EInvoicingApp from "@/pages/EInvoicingApp";
import EInvoiceContract from "@/pages/EInvoiceContract";
import StandardInvoices from "@/pages/StandardInvoices";
import JamClearance from "@/pages/JamClearance";
import CreditNotes from "@/pages/CreditNotes";
import { homeFor } from "@/lib/homeFor";
import RfqDetail from "@/pages/RfqDetail";
import QuotationView from "@/pages/QuotationView";
import Tracker from "@/pages/Tracker";
import Settings from "@/pages/Settings";
import QuotationTemplates from "@/pages/QuotationTemplates";
import Marketplace from "@/pages/Marketplace";
import VendorPublic from "@/pages/VendorPublic";
import HowItWorks from "@/pages/HowItWorks";
import Faq from "@/pages/Faq";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Billing from "@/pages/Billing";
import Admin from "@/pages/Admin";
import BuyerRfqs from "@/pages/BuyerRfqs";
import ContractorProfile from "@/pages/ContractorProfile";
import Referrals from "@/pages/Referrals";
import Qompare from "@/pages/Qompare";
import MyVendors from "@/pages/MyVendors";
import BuyerRfqDetail from "@/pages/BuyerRfqDetail";
import Comparison from "@/pages/Comparison";
import IncomingRfq from "@/pages/IncomingRfq";
import Pricing from "@/pages/Pricing";
import QReview from "@/pages/QReview";
import ForVendors from "@/pages/ForVendors";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import { Loader2 } from "lucide-react";

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="w-6 h-6 animate-spin text-primary" />
  </div>
);

function Guard({ children, need }) {
  const { user, ready } = useAuth();
  if (!ready) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  const isContractor = user.account_type === "contractor";
  const isEinvoice = user.account_type === "einvoice";
  // e-invoicing accounts have no marketplace/vendor/contractor tools
  if (isEinvoice && (need === "vendor" || need === "contractor")) return <Navigate to="/einvoicing/app" replace />;
  if (need === "vendor" && isContractor) return <Navigate to="/marketplace" replace />;
  if (need === "contractor" && !isContractor) return <Navigate to={homeFor(user)} replace />;
  if (need === "admin" && !user.is_admin) return <Navigate to={homeFor(user)} replace />;
  return <Layout>{children}</Layout>;
}

function PublicOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <Spinner />;
  if (user) {
    const dest = user.account_type === "contractor" ? "/marketplace"
      : user.account_type === "einvoice" ? "/einvoicing/app" : "/for-vendors";
    return <Navigate to={dest} replace />;
  }
  return children;
}

function MarketplaceRoute() {
  const { user, ready } = useAuth();
  if (!ready) return <Spinner />;
  // Marketplace is a contractor-only tool; signed-in vendors are sent to their own landing.
  // e-invoicing users may opt in via the callout, so let them view it.
  if (user && user.account_type === "vendor") return <Navigate to="/for-vendors" replace />;
  return user ? <Layout><Marketplace embedded /></Layout> : <Marketplace />;
}

function RoleRedirect() {
  const { user, ready } = useAuth();
  if (!ready) return <Spinner />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.account_type === "einvoice") return <Navigate to="/einvoicing/app" replace />;
  return <Navigate to={user.account_type === "contractor" ? "/rfqs" : "/tracker"} replace />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/marketplace" element={<MarketplaceRoute />} />
            <Route path="/vendor/:id" element={<VendorPublic />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/einvoicing" element={<EInvoicingService />} />
            <Route path="/for-vendors" element={<ForVendors />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/dashboard" element={<Guard need="vendor"><Dashboard /></Guard>} />
            <Route path="/catalogue" element={<Guard need="vendor"><Catalogue /></Guard>} />
            <Route path="/quotation-templates" element={<Guard need="vendor"><QuotationTemplates /></Guard>} />
            <Route path="/einvoices" element={<Navigate to="/einvoicing/app" replace />} />
            <Route path="/einvoicing/app" element={<Guard><EInvoicingApp /></Guard>} />
            <Route path="/einvoicing/standard" element={<Guard><StandardInvoices /></Guard>} />
            <Route path="/einvoicing/clearance" element={<Guard><JamClearance /></Guard>} />
            <Route path="/einvoicing/credit-notes" element={<Guard><CreditNotes /></Guard>} />
            <Route path="/einvoicing/contract/:id" element={<Guard><EInvoiceContract /></Guard>} />
            <Route path="/rfq/:id" element={<Guard need="vendor"><RfqDetail /></Guard>} />
            <Route path="/rfq/:id/quotation" element={<Guard need="vendor"><QuotationView /></Guard>} />
            <Route path="/tracker" element={<Guard need="vendor"><Tracker /></Guard>} />
            <Route path="/qreview" element={<Guard><QReview /></Guard>} />
            <Route path="/referrals" element={<Guard><Referrals /></Guard>} />
            <Route path="/incoming/:id" element={<Guard need="vendor"><IncomingRfq /></Guard>} />
            <Route path="/settings" element={<Guard><Settings /></Guard>} />
            <Route path="/opportunities" element={<RoleRedirect />} />
            <Route path="/rfqs" element={<Guard need="contractor"><BuyerRfqs /></Guard>} />
            <Route path="/profile" element={<Guard need="contractor"><ContractorProfile /></Guard>} />
            <Route path="/qompare" element={<Guard need="contractor"><Qompare /></Guard>} />
            <Route path="/my-vendors" element={<Guard need="contractor"><MyVendors /></Guard>} />
            <Route path="/rfqs/:id" element={<Guard need="contractor"><BuyerRfqDetail /></Guard>} />
            <Route path="/compare/:id" element={<Guard need="contractor"><Comparison /></Guard>} />
            <Route path="/billing" element={<Guard><Billing /></Guard>} />
            <Route path="/admin" element={<Guard need="admin"><Admin /></Guard>} />
            <Route path="/payment/success" element={<Guard><PaymentSuccess /></Guard>} />
            <Route path="/payment/cancel" element={<Guard><PaymentCancel /></Guard>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
